import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import Card from '@/components/elements/Card/Card';
import { Button } from '@/components/elements/forms/buttons';
import { LoadingIcon } from '@/components/icons';
import Icon from '@/components/icons/Icon';
import { SearchInfoModal } from '@/components/modules/pages/serp/SearchInfoWithPopup';
import { SortedBy, SortingManagerOutput } from '@/features/searchV2/components/Sorting/Sorting.types';
import { classnames, isServer } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { Fragment, useState } from 'react';
import { createPortal } from 'react-dom';

type SortTriggerProps = SortingManagerOutput & {
  options: SortedBy[];
  activeSort: SortedBy;
  loading: boolean;
};

const SortTrigger = ({ options, activeSort, loading, ...sortingManager }: SortTriggerProps) => {
  const { isMobileView } = useMobileView();
  const [showInfo, setShowInfo] = useState(false);
  const infoLabel = _s('searchInfoBanner.link');
  const infoModal = <SearchInfoModal isOpen={showInfo} onClose={() => setShowInfo(false)} />;

  const trigger = (
    <Button
      variant="link"
      onClick={isMobileView ? () => sortingManager.onShowOptions(true) : undefined}
      rightIcon={
        loading ? (
          <LoadingIcon style={{ width: 20, height: 20 }} />
        ) : (
          <Icon color="fg_link" variant="arrow-up-down" style={{ width: 20, height: 20 }} />
        )
      }>
      <span className="text-m flex whitespace-nowrap">{_s(activeSort.label)}</span>
    </Button>
  );

  const radioIcon = (isActive: boolean) => (
    <Icon variant={isActive ? 'radio-checked' : 'radio-unchecked'} className="flex-shrink-0" />
  );

  if (isMobileView) {
    return (
      <Fragment>
        {trigger}
        {sortingManager.showOptions &&
          !isServer &&
          createPortal(
            <Fragment>
              {/* must beat the map carousel's z-9999 */}
              <div className="bg-scrim-40 fixed inset-0 z-[10000]"></div>
              <Card
                bottom
                closable={false}
                onClickOutside={() => sortingManager.onShowOptions(false)}
                className="fixed bottom-0 left-0 right-0 z-[10001] text-left">
                <span className="text-capitalize text-xxs text-fg_secondary pb-md block">{_s('sortBy')}</span>
                <ul className="space-y-sm" role="radiogroup">
                  {options.map((option) => {
                    const isActive = option.value === activeSort.value;
                    return (
                      <li
                        key={option.value}
                        className="gap-sm flex cursor-pointer flex-row-reverse items-center"
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => sortingManager.onChangeSorting(option.value)}>
                        <span className="flex-1">{_s(option.label)}</span>
                        {radioIcon(isActive)}
                      </li>
                    );
                  })}
                </ul>
                <Button
                  variant="link"
                  className="self-start !px-0"
                  label={infoLabel}
                  onClick={() => {
                    sortingManager.onShowOptions(false);
                    setShowInfo(true);
                  }}
                />
              </Card>
            </Fragment>,
            document.body,
          )}
        {infoModal}
      </Fragment>
    );
  }

  return (
    <div role="group" className="relative">
      <DropdownMenu initialState={{ isOpen: false }} preventScrollFocus>
        <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
        <DropdownMenu.Content
          className={classnames(
            'p-xs space-y-2xs bg-bg_neutral',
            'border-border_focus -mx-[3px] rounded-md border-[3px]',
          )}>
          {options.map((option) => {
            const isActive = option.value === activeSort.value;
            return (
              <DropdownMenu.Item
                key={option.value}
                role="menuitemradio"
                checked={isActive}
                onSelect={() => sortingManager.onChangeSorting(option.value)}
                className="gap-xs h-3xl px-sm flex items-center rounded-sm">
                <span className="flex-1">{_s(option.label)}</span>
                {radioIcon(isActive)}
              </DropdownMenu.Item>
            );
          })}
          {/* opens after the menu's close() has refocused the trigger, which would otherwise pull focus out of the modal */}
          <DropdownMenu.Item
            onSelect={() => setTimeout(() => setShowInfo(true))}
            className="h-3xl px-sm flex items-center rounded-sm">
            <span className="text-fg_link hover:text-fg_link_hover text-l underline">{infoLabel}</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu>
      {infoModal}
    </div>
  );
};

export default SortTrigger;
